import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { TenantCapabilities } from '@/shared/api/tenantSettings';
import { activeNavTarget, mobileItems } from '@/shared/navigation/manifest';

import { Icon } from './Icon';
import classes from './MobileTabBar.module.css';

/** Five-slot bottom bar on phones (13 §2.2): up to four items from the manifest plus "Más". */
export function MobileTabBar({
  permissions,
  capabilities,
  onMore,
}: {
  permissions: ReadonlySet<string>;
  capabilities: TenantCapabilities;
  onMore: () => void;
}) {
  const { t } = useTranslation('nav');
  const items = mobileItems(permissions, capabilities);
  const location = useLocation();
  const lit = activeNavTarget(location.pathname, location.search);

  return (
    <nav className={classes.bar} aria-label={t('aria.mobile')}>
      {items.map((item) =>
        item.implemented ? (
          <Link
            key={item.to}
            to={item.to}
            className={classes.tab}
            activeProps={{ className: `${classes.tab} ${classes.active}` }}
            activeOptions={{ exact: lit !== item.to }}
          >
            <Icon name={item.icon} size={24} />
            <span>{t(item.labelKey)}</span>
          </Link>
        ) : (
          <span key={item.to} className={`${classes.tab} ${classes.soon}`} aria-disabled="true">
            <Icon name={item.icon} size={24} />
            <span>{t(item.labelKey)}</span>
          </span>
        ),
      )}
      <button type="button" className={classes.tab} onClick={onMore}>
        <Icon name="more" size={24} />
        <span>{t('item.more')}</span>
      </button>
    </nav>
  );
}
